import React, { useState } from 'react';
import { SmartSelect } from '../dropdown/SmartSelect';
import { updateSubscription } from '../../services/esetApi';
import type { EsetSubscription } from '../../services/esetApi';
import { useEsetBundles } from '../../hooks/useEsetBundles';

interface EsetLicenseEditProps {
  sub: EsetSubscription;
  onSaved: () => void;
  onCancel: () => void;
}

// action_on_expiry is a free string on the backend; the only value the manage
// lifecycle acts on is "lapse" (void unpaid invoice + suspend + mark lapsed).
// Anything else is the default "keep / renew" state.
const ON_EXPIRY_OPTIONS = [
  { value: 'renew', label: 'Keep / renew (default)' },
  { value: 'lapse', label: 'Lapse on expiry' },
];

// A multi-year term bills the 1-year line-up once per year on one renewal.
const TERM_OPTIONS = [
  { value: '1', label: '1 year (default)' },
  { value: '2', label: '2 years' },
  { value: '3', label: '3 years' },
];

export const EsetLicenseEdit: React.FC<EsetLicenseEditProps> = ({ sub, onSaved, onCancel }) => {
  const bundles = useEsetBundles();
  const initialExpiry = (sub.action_on_expiry || '').toLowerCase() === 'lapse' ? 'lapse' : 'renew';
  const [bundleGuid, setBundleGuid] = useState<string | null>(null); // null = keep current bundle
  const [onExpiry, setOnExpiry] = useState(initialExpiry);
  const initialTerm = String(sub.term_years || 1);
  const [term, setTerm] = useState(initialTerm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changed = Boolean(bundleGuid) || onExpiry !== initialExpiry || term !== initialTerm;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const updates: { product_id?: string; action_on_expiry?: string; term_years?: number } = {};
      if (bundleGuid) updates.product_id = bundleGuid;
      if (onExpiry !== initialExpiry) updates.action_on_expiry = onExpiry;
      if (term !== initialTerm) updates.term_years = Number(term);
      await updateSubscription(sub.id, updates);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      setBusy(false);
    }
  };

  return (
    <div className="eset-lic-edit">
      <div className="eset-lic-edit__current">
        Current bundle: <strong>{sub.product_bundle ?? '—'}</strong>
        {sub.commercial_product && (
          <>
            {' '}
            — {sub.commercial_product}, {sub.billing_class}. The bundle sets the product and class
            only; seats always follow the ESET licensed units.
          </>
        )}
      </div>

      <SmartSelect
        label="Product / class (bundle)"
        value={bundleGuid}
        options={bundles.map((b) => ({ value: b.guid, label: b.sku }))}
        onChange={setBundleGuid}
        placeholder="Keep current…"
      />

      <SmartSelect
        label="Term"
        value={term}
        options={TERM_OPTIONS}
        onChange={(v) => setTerm(v ?? '1')}
      />

      <SmartSelect
        label="On expiry"
        value={onExpiry}
        options={ON_EXPIRY_OPTIONS}
        onChange={(v) => setOnExpiry(v ?? 'renew')}
      />

      {error && <div className="eset-lic-edit__error">{error}</div>}

      <div className="eset-lic-edit__actions">
        <button className="eset-panel__btn-cancel" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className="eset-prov__action eset-prov__action--primary"
          onClick={save}
          disabled={busy || !changed}
        >
          Save
        </button>
      </div>
    </div>
  );
};
